import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Search, Plus, Play, Trash2, Eye, ChevronLeft, ChevronRight, Upload, X, Sparkles, MoreVertical, Copy, Tag } from 'lucide-react';
import api, { executionPlanApi, testApi, categoriesApi } from '../services/api';
import DuplicateGoalModal from '../components/DuplicateGoalModal';
import BulkCategorizeModal from '../components/BulkCategorizeModal';
import TimeAgo from '../components/TimeAgo';
import Dialog from '../components/Dialog';
import { useDialog } from '../hooks/useDialog';
import { useWorkspace } from '../context/WorkspaceContext';
import Pagination from '../components/Pagination';
import './GoalsList.css';

const GoalsList = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { dialogState, closeDialog, confirm, alert } = useDialog();

    const { currentWorkspace } = useWorkspace();
    const [goals, setGoals] = useState([]);
    const [apps, setApps] = useState([]);
    const [loading, setLoading] = useState(true);

    // Initialize state from URL params
    const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchParams.get('search') || '');
    const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
    const [appIdFilter, setAppIdFilter] = useState(searchParams.get('appId') || '');
    const [sortBy, setSortBy] = useState(searchParams.get('sortBy') || 'createdAt');
    const [sortOrder, setSortOrder] = useState(searchParams.get('sortOrder') || 'DESC');
    const [pagination, setPagination] = useState({
        page: parseInt(searchParams.get('page')) || 1,
        limit: parseInt(searchParams.get('limit')) || parseInt(localStorage.getItem('rova_limit_goals')) || 20,
        total: 0,
        totalPages: 0
    });

    const [workspaceCategories, setWorkspaceCategories] = useState([]);
    const [selectedFilterCategoryIds, setSelectedFilterCategoryIds] = useState(
        searchParams.get('categoryIds') ? searchParams.get('categoryIds').split(',').filter(Boolean) : []
    );

    const [selectedGoalIds, setSelectedGoalIds] = useState([]);

    // Kebab Menu and Duplication States
    const [activeActionMenuGoalId, setActiveActionMenuGoalId] = useState(null);
    const [selectedGoalForDuplication, setSelectedGoalForDuplication] = useState(null);
    const [showBulkCategorizeModal, setShowBulkCategorizeModal] = useState(false);

    // Bulk Run Modal & Execution Mode States
    const [showBulkRunModal, setShowBulkRunModal] = useState(false);
    const [bulkRunMode, setBulkRunMode] = useState('individual'); // 'individual' or 'execution_plan'
    const [bulkPlanName, setBulkPlanName] = useState('');
    const [isSubmittingBulkRun, setIsSubmittingBulkRun] = useState(false);

    const [existingPlans, setExistingPlans] = useState([]);
    const [showAddToPlanModal, setShowAddToPlanModal] = useState(false);
    const [planSearchQuery, setPlanSearchQuery] = useState('');
    const [isSubmittingPlanAction, setIsSubmittingPlanAction] = useState(false);

    const [showCreatePlanModal, setShowCreatePlanModal] = useState(false);
    const [newPlanName, setNewPlanName] = useState('');
    const [newPlanDescription, setNewPlanDescription] = useState('');
    const [newPlanAppId, setNewPlanAppId] = useState('');


    // Debounce search term (500ms delay)
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 500);

        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Update URL when state changes
    useEffect(() => {
        const params = new URLSearchParams();

        params.set('page', pagination.page.toString());
        params.set('limit', pagination.limit.toString());
        params.set('sortBy', sortBy);
        params.set('sortOrder', sortOrder);

        if (debouncedSearchTerm) params.set('search', debouncedSearchTerm);
        if (statusFilter) params.set('status', statusFilter);
        if (appIdFilter) params.set('appId', appIdFilter);
        if (selectedFilterCategoryIds.length > 0) params.set('categoryIds', selectedFilterCategoryIds.join(','));

        setSearchParams(params, { replace: true });
    }, [pagination.page, pagination.limit, debouncedSearchTerm, statusFilter, appIdFilter, sortBy, sortOrder, selectedFilterCategoryIds]);

    useEffect(() => {
        setSelectedGoalIds([]);
    }, [pagination.page, pagination.limit, debouncedSearchTerm, statusFilter, appIdFilter, sortBy, sortOrder, selectedFilterCategoryIds]);

    useEffect(() => {
        const handleOutsideClick = () => {
            setActiveActionMenuGoalId(null);
        };
        document.addEventListener('click', handleOutsideClick);
        return () => {
            document.removeEventListener('click', handleOutsideClick);
        };
    }, []);

    useEffect(() => {
        let isMounted = true;

        const loadGoals = async () => {
            if (!isMounted) return;
            await fetchGoals();
        };

        loadGoals();

        return () => {
            isMounted = false;
        };
    }, [pagination.page, pagination.limit, debouncedSearchTerm, statusFilter, appIdFilter, sortBy, sortOrder, selectedFilterCategoryIds]);

    useEffect(() => {
        const fetchApps = async () => {
            if (!currentWorkspace?.id) return;
            try {
                const response = await api.get('/apps', {
                    params: { workspaceId: currentWorkspace.id, limit: 100 }
                });
                setApps(response.data.rows || []);
            } catch (error) {
                console.error('Error fetching apps:', error);
            }
        };
        fetchApps();
    }, [currentWorkspace?.id]);

    useEffect(() => {
        const fetchCategories = async () => {
            if (!currentWorkspace?.id) return;
            try {
                const response = await categoriesApi.list(currentWorkspace.id);
                setWorkspaceCategories(response.data.categories || []);
            } catch (error) {
                console.error('Error fetching categories:', error);
            }
        };
        fetchCategories();
    }, [currentWorkspace?.id]);

    const fetchGoals = async () => {
        try {
            setLoading(true);
            const params = {
                page: pagination.page,
                limit: pagination.limit,
                sortBy,
                sortOrder
            };

            if (debouncedSearchTerm) params.search = debouncedSearchTerm;
            if (statusFilter) params.status = statusFilter;
            if (appIdFilter) params.appId = appIdFilter;
            if (selectedFilterCategoryIds.length > 0) {
                params.categoryIds = selectedFilterCategoryIds.join(',');
            }

            const response = await api.get('/tests/goals', { params });

            setGoals(response.data.goals);
            setPagination(prev => ({
                ...prev,
                total: response.data.pagination.total,
                totalPages: response.data.pagination.totalPages
            }));
        } catch (error) {
            console.error('Error fetching goals:', error);
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (newPage) => {
        setPagination(prev => ({ ...prev, page: newPage }));
    };

    const handleLimitChange = (newLimit) => {
        setPagination(prev => ({ ...prev, limit: newLimit, page: 1 }));
    };

    const handleSelectGoal = (goalId) => {
        setSelectedGoalIds(prev =>
            prev.includes(goalId)
                ? prev.filter(id => id !== goalId)
                : [...prev, goalId]
        );
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedGoalIds(goals.map(g => g.id));
        } else {
            setSelectedGoalIds([]);
        }
    };

    const handleBulkRun = () => {
        setBulkRunMode('individual');
        setBulkPlanName('');
        setShowBulkRunModal(true);
    };

    const executeBulkRun = async () => {
        setIsSubmittingBulkRun(true);
        try {
            const response = await testApi.bulkRerunTestGoals(selectedGoalIds, {
                executionMode: bulkRunMode,
                planName: bulkPlanName.trim() || undefined
            });

            setShowBulkRunModal(false);
            setSelectedGoalIds([]);

            if (response.data.executionMode === 'execution_plan' && response.data.executionPlanExecutionId) {
                const planExecId = response.data.executionPlanExecutionId;
                await alert({
                    title: 'Execution Plan Started',
                    message: (
                        <span>
                            Execution plan created and started!{' '}
                            <Link to={`/execution-plans/executions/${planExecId}`} style={{ color: 'var(--primary, #6366f1)', fontWeight: 'bold', textDecoration: 'underline' }}>
                                View Live Plan Progress →
                            </Link>
                        </span>
                    ),
                    variant: 'success'
                });
            } else {
                await alert({
                    title: 'Success',
                    message: `Successfully queued ${selectedGoalIds.length} goals for individual execution!`,
                    variant: 'success'
                });
            }
            fetchGoals();
        } catch (error) {
            console.error('Error executing goals in bulk:', error);
            await alert({
                title: 'Error',
                message: error.response?.data?.error || 'Failed to trigger execution for selected goals.',
                variant: 'danger'
            });
        } finally {
            setIsSubmittingBulkRun(false);
        }
    };

    const handleBulkExport = () => {
        try {
            const selectedGoals = goals.filter(g => selectedGoalIds.includes(g.id));
            const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(selectedGoals, null, 2));
            const downloadAnchor = document.createElement('a');
            downloadAnchor.setAttribute("href", dataStr);
            downloadAnchor.setAttribute("download", `rova_exported_goals_${new Date().toISOString().split('T')[0]}.json`);
            document.body.appendChild(downloadAnchor);
            downloadAnchor.click();
            downloadAnchor.remove();

            setSelectedGoalIds([]);
        } catch (error) {
            console.error('Error exporting goals:', error);
        }
    };

    const handleBulkDelete = async () => {
        const confirmed = await confirm({
            title: 'Delete Selected Goals',
            message: `Are you sure you want to delete the ${selectedGoalIds.length} selected test goals? This action cannot be undone.`,
            confirmText: 'Delete All',
            cancelText: 'Cancel',
            variant: 'danger'
        });

        if (!confirmed) return;

        try {
            setLoading(true);
            await testApi.bulkDeleteTestGoals(selectedGoalIds);
            setSelectedGoalIds([]);
            fetchGoals();
        } catch (error) {
            console.error('Error deleting goals in bulk:', error);
            await alert({
                title: 'Error',
                message: error.response?.data?.error || 'Failed to delete selected test goals. Please try again.',
                variant: 'danger'
            });
        } finally {
            setLoading(false);
        }
    };

    const loadExistingPlans = async () => {
        try {
            const response = await executionPlanApi.getExecutionPlans({
                page: 1,
                limit: 100
            });
            setExistingPlans(response.data.executionPlans || []);
        } catch (err) {
            console.error('Failed to load plans:', err);
        }
    };

    const handleAddToPlan = async (planId) => {
        setIsSubmittingPlanAction(true);
        try {
            await executionPlanApi.bulkAddGoalsToPlan(planId, selectedGoalIds);
            setShowAddToPlanModal(false);
            setSelectedGoalIds([]);
            setIsSubmittingPlanAction(false);
            await alert({
                title: 'Success',
                message: (
                    <span>
                        Successfully added selected goals to the plan!{' '}
                        <Link to={`/execution-plans/${planId}`} style={{ color: 'var(--primary, #6366f1)', textDecoration: 'underline', fontWeight: 'bold' }}>
                            Go to plan
                        </Link>
                    </span>
                ),
                variant: 'success'
            });
        } catch (err) {
            console.error(err);
            setShowAddToPlanModal(false);
            setIsSubmittingPlanAction(false);
            await alert({
                title: 'Error',
                message: err.response?.data?.error || 'Failed to add goals to the plan.',
                variant: 'danger'
            });
        } finally {
            setIsSubmittingPlanAction(false);
        }
    };

    const handleOpenCreatePlan = () => {
        const selectedGoals = goals.filter(g => selectedGoalIds.includes(g.id));
        const platforms = Array.from(new Set(selectedGoals.map(g => g.platform)));
        if (platforms.length > 1) {
            alert({
                title: 'Invalid Selection',
                message: 'All selected test goals must belong to the same platform (iOS or Android) to create an execution plan.',
                variant: 'danger'
            });
            return;
        }
        // Set first matching app by default if available
        const targetPlatform = platforms[0] || 'android';
        const matchingApps = apps.filter(app => app.platform === targetPlatform);
        if (matchingApps.length > 0) {
            setNewPlanAppId(matchingApps[0].id);
        } else {
            setNewPlanAppId('');
        }
        setNewPlanName('');
        setNewPlanDescription('');
        setShowCreatePlanModal(true);
    };

    const handleCreateNewPlan = async (e) => {
        e.preventDefault();
        if (!newPlanName.trim()) {
            await alert({ title: 'Validation Error', message: 'Please enter a plan name.', variant: 'danger' });
            return;
        }
        if (!newPlanAppId) {
            await alert({ title: 'Validation Error', message: 'Please select an App.', variant: 'danger' });
            return;
        }

        setIsSubmittingPlanAction(true);
        try {
            const selectedGoals = goals.filter(g => selectedGoalIds.includes(g.id));
            const targetPlatform = selectedGoals[0]?.platform || 'android';
            const quotas = currentWorkspace?.subscription?.quotas || {};
            const realDevicesEnabled = quotas.realDevicesEnabled === true;

            let deviceProvider = 'aws';
            let deviceArn = 'auto';
            if (targetPlatform === 'android' && !realDevicesEnabled) {
                deviceProvider = 'emulator';
                deviceArn = 'emulator';
            }

            const response = await executionPlanApi.createExecutionPlan({
                name: newPlanName,
                description: newPlanDescription,
                appId: newPlanAppId,
                goalIds: selectedGoalIds,
                platform: targetPlatform,
                deviceProvider,
                deviceArn,
                sourceType: 'manual',
                executionMode: 'sequential',
                stopOnFailure: true,
                retryFailedGoals: true,
                maxRetries: 2
            });

            const createdPlanId = response.data.executionPlan?.id;

            setShowCreatePlanModal(false);
            setSelectedGoalIds([]);
            setIsSubmittingPlanAction(false);
            await alert({
                title: 'Success',
                message: (
                    <span>
                        Execution plan created successfully with selected goals!{' '}
                        {createdPlanId && (
                            <Link to={`/execution-plans/${createdPlanId}`} style={{ color: 'var(--primary, #6366f1)', textDecoration: 'underline', fontWeight: 'bold' }}>
                                Go to plan
                            </Link>
                        )}
                    </span>
                ),
                variant: 'success'
            });
        } catch (err) {
            console.error(err);
            setShowCreatePlanModal(false);
            setIsSubmittingPlanAction(false);
            await alert({
                title: 'Error',
                message: err.response?.data?.error || 'Failed to create execution plan.',
                variant: 'danger'
            });
        } finally {
            setIsSubmittingPlanAction(false);
        }
    };

    const handleRerun = async (goalId, e) => {
        e.stopPropagation();
        try {
            const response = await api.post(`/tests/goals/${goalId}/rerun`);
            navigate(`/test/execution/${response.data.execution.id}`);
        } catch (error) {
            console.error('Error rerunning goal:', error);
            const errorMessage = error.response?.data?.error || 'Failed to rerun test goal.'
            let displayMessage = errorMessage;
            if (errorMessage.toLowerCase().includes('quota exceeded')) {
                displayMessage = (
                    <span>
                        {errorMessage}.{' '}
                        <Link to="/settings/billing" style={{ textDecoration: 'underline' }}>
                            Upgrade
                        </Link>
                    </span>
                );
            }
            await alert({
                title: 'Error',
                message: displayMessage,
                variant: 'danger'
            });
        }
    };

    const handleDelete = async (goalId, e) => {
        e.stopPropagation();

        const confirmed = await confirm({
            title: 'Delete Test Goal',
            message: 'Are you sure you want to delete this test goal? This action cannot be undone.',
            confirmText: 'Delete',
            cancelText: 'Cancel',
            variant: 'danger'
        });

        if (!confirmed) return;

        try {
            await api.delete(`/tests/goals/${goalId}`);
            fetchGoals();
        } catch (error) {
            console.error('Error deleting goal:', error);
            await alert({
                title: 'Error',
                message: 'Failed to delete test goal. Please try again.',
                variant: 'danger'
            });
        }
    };

    const getStatusBadge = (status) => {
        status = status.toLowerCase();
        const statusMap = {
            success: { label: 'success', className: 'status-success' },
            failed: { label: 'fail', className: 'status-failed' },
            error: { label: 'error', className: 'status-error' },
            running: { label: 'running', className: 'status-running' },
            queued: { label: 'queued', className: 'status-queued' },
            cancelled: { label: 'cancelled', className: 'status-cancelled' },
            inconclusive: { label: 'inconclusive', className: 'status-inconclusive' }
        };

        const config = statusMap[status] || { label: 'unknown', className: 'status-unknown' };
        return <span className={`status-badge ${config.className}`}>{config.label}</span>;
    };

    const filteredPlans = existingPlans.filter(p =>
        p.name.toLowerCase().includes(planSearchQuery.toLowerCase()) ||
        (p.description && p.description.toLowerCase().includes(planSearchQuery.toLowerCase()))
    );

    return (
        <div className="goals-list-container">
            <div className="goals-header">
                <h1>
                    Test Goals
                    <span className="title-badge">{pagination.total || 0}</span>
                </h1>
                <div style={{ display: 'flex', gap: '10px' }}>
                    <button className="btn btn-secondary" onClick={() => navigate('/goals/import')} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                        <Upload size={18} />
                        Import Goals
                    </button>
                    <button className="btn btn-primary" onClick={() => navigate('/test-goals')}>
                        <Plus size={18} />
                        New Goal
                    </button>
                </div>
            </div>

            <div className="goals-filters">
                <div className="search-box">
                    <Search size={18} />
                    <input
                        type="text"
                        placeholder="Search goals..."
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setPagination(prev => ({ ...prev, page: 1 }));
                        }}
                    />
                </div>

                <select
                    value={appIdFilter}
                    onChange={(e) => {
                        setAppIdFilter(e.target.value);
                        setPagination(prev => ({ ...prev, page: 1 }));
                    }}
                    className="filter-select"
                >
                    <option value="">All Apps</option>
                    {apps.map(app => (
                        <option key={app.id} value={app.id}>
                            {app.displayName || app.name} ({app.platform})
                        </option>
                    ))}
                </select>

                <select
                    value={statusFilter}
                    onChange={(e) => {
                        setStatusFilter(e.target.value);
                        setPagination(prev => ({ ...prev, page: 1 }));
                    }}
                    className="filter-select"
                >
                    <option value="">All Status</option>
                    <option value="success">Passed</option>
                    <option value="failed">Failed</option>
                    <option value="error">Error</option>
                </select>

                <select
                    value={`${sortBy}-${sortOrder}`}
                    onChange={(e) => {
                        const [newSortBy, newSortOrder] = e.target.value.split('-');
                        setSortBy(newSortBy);
                        setSortOrder(newSortOrder);
                    }}
                    className="filter-select"
                >
                    <option value="createdAt-DESC">Newest First</option>
                    <option value="createdAt-ASC">Oldest First</option>
                    <option value="executionCount-DESC">Most Runs</option>
                </select>

                {/* Category Multi-select Filter */}
                <select
                    value=""
                    onChange={(e) => {
                        const val = e.target.value;
                        if (val && !selectedFilterCategoryIds.includes(val)) {
                            setSelectedFilterCategoryIds(prev => [...prev, val]);
                            setPagination(prev => ({ ...prev, page: 1 }));
                        }
                    }}
                    className="filter-select"
                >
                    <option value="">Filter Categories</option>
                    {workspaceCategories
                        .filter(cat => !selectedFilterCategoryIds.includes(cat.id))
                        .map(cat => (
                            <option key={cat.id} value={cat.id}>{cat.name}</option>
                        ))
                    }
                </select>
            </div>

            {/* Selected Category Badges */}
            {selectedFilterCategoryIds.length > 0 && (
                <div className="active-filters" style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', margin: '-10px 0 15px 0' }}>
                    <span style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'inline-flex', alignItems: 'center' }}>Active filters:</span>
                    {selectedFilterCategoryIds.map(id => {
                        const cat = workspaceCategories.find(c => c.id === id);
                        if (!cat) return null;
                        return (
                            <span
                                key={id}
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    padding: '2px 8px',
                                    borderRadius: '12px',
                                    fontSize: '12px',
                                    fontWeight: '500',
                                    backgroundColor: cat.color ? `${cat.color}20` : '#94a3b820',
                                    color: cat.color || '#94a3b8',
                                    border: `1px solid ${cat.color || '#94a3b8'}40`
                                }}
                            >
                                {cat.name}
                                <X
                                    size={12}
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => {
                                        setSelectedFilterCategoryIds(prev => prev.filter(cId => cId !== id));
                                        setPagination(prev => ({ ...prev, page: 1 }));
                                    }}
                                />
                            </span>
                        );
                    })}
                    <button
                        onClick={() => {
                            setSelectedFilterCategoryIds([]);
                            setPagination(prev => ({ ...prev, page: 1 }));
                        }}
                        style={{
                            border: 'none',
                            background: 'none',
                            color: 'var(--primary-color)',
                            fontSize: '12px',
                            cursor: 'pointer',
                            padding: '0 4px',
                            textDecoration: 'underline'
                        }}
                    >
                        Clear All
                    </button>
                </div>
            )}

            {loading ? (
                <div className="loading-state">Loading goals...</div>
            ) : goals.length === 0 ? (
                <div className="empty-state">
                    <p>No test goals found</p>
                    <button className="btn btn-primary" onClick={() => navigate('/test-goals')}>
                        Create Your First Goal
                    </button>
                </div>
            ) : (
                <>
                    {selectedGoalIds.length > 0 && (
                        <div className="bulk-actions-toolbar">
                            <div className="bulk-info">
                                <strong>{selectedGoalIds.length}</strong> goal{selectedGoalIds.length !== 1 ? 's' : ''} selected
                            </div>
                            <div className="bulk-buttons">
                                <button className="btn btn-secondary btn-sm" onClick={handleBulkRun}>
                                    <Play size={14} />
                                    Run Selected
                                </button>
                                <button className="btn btn-secondary btn-sm" onClick={() => { loadExistingPlans(); setShowAddToPlanModal(true); }}>
                                    <Plus size={14} />
                                    Add to Plan
                                </button>
                                <button className="btn btn-secondary btn-sm" onClick={handleOpenCreatePlan}>
                                    <Sparkles size={14} />
                                    Create Plan
                                </button>
                                <button className="btn btn-secondary btn-sm" onClick={handleBulkExport}>
                                    <Upload size={14} />
                                    Export Selected
                                </button>
                                <button className="btn btn-secondary btn-sm" onClick={() => setShowBulkCategorizeModal(true)}>
                                    <Tag size={14} />
                                    Add to Categories
                                </button>
                                <button className="btn btn-danger btn-sm" onClick={handleBulkDelete}>
                                    <Trash2 size={14} />
                                    Delete Selected
                                </button>
                            </div>
                        </div>
                    )}

                    <div className="goals-table-container">
                        <table className="goals-table">
                            <thead>
                                <tr>
                                    <th className="checkbox-cell">
                                        <input
                                            type="checkbox"
                                            checked={goals.length > 0 && selectedGoalIds.length === goals.length}
                                            onChange={handleSelectAll}
                                        />
                                    </th>
                                    <th>Last Execution</th>
                                    <th>Test Goal</th>
                                    <th>Platform</th>
                                    <th>Runs</th>
                                    <th>Last Run</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {goals.map((goal) => (
                                    <tr key={goal.id} className={`goal-row ${selectedGoalIds.includes(goal.id) ? 'selected' : ''}`}>
                                        <td className="checkbox-cell" onClick={(e) => e.stopPropagation()}>
                                            <input
                                                type="checkbox"
                                                checked={selectedGoalIds.includes(goal.id)}
                                                onChange={() => handleSelectGoal(goal.id)}
                                            />
                                        </td>
                                        <td>
                                            {goal.lastStatus ? (
                                                getStatusBadge(goal.lastStatus)
                                            ) : (
                                                <span className="status-badge status-unknown">—</span>
                                            )}
                                        </td>
                                        <td className="goal-name-cell" style={{ verticalAlign: 'top', padding: '12px' }}>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                                    <Link to={`/goals/${goal.id}`} className="goal-name-link" style={{ fontWeight: '600', fontSize: '14px', color: 'var(--text-primary)' }}>
                                                        {goal.title || (goal.goalText.length > 50 ? goal.goalText.substring(0, 50) + '...' : goal.goalText)}
                                                    </Link>

                                                    {/* Category Badges */}
                                                    {goal.categories && goal.categories.map(cat => (
                                                        <span
                                                            key={cat.id}
                                                            style={{
                                                                display: 'inline-flex',
                                                                padding: '2px 6px',
                                                                borderRadius: '4px',
                                                                fontSize: '11px',
                                                                fontWeight: '600',
                                                                backgroundColor: cat.color ? `${cat.color}15` : '#94a3b815',
                                                                color: cat.color || '#94a3b8',
                                                                border: `1px solid ${cat.color || '#94a3b8'}25`
                                                            }}
                                                        >
                                                            {cat.name}
                                                        </span>
                                                    ))}
                                                </div>
                                                <span style={{ fontSize: '12px', color: 'var(--text-secondary)', WebkitLineClamp: 2, display: '-webkit-box', WebkitBoxOrient: 'vertical', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                    {goal.goalText}
                                                </span>
                                            </div>
                                        </td>
                                        <td>
                                            <span className={`platform-badge platform-${goal.platform}`}>
                                                {goal.platform === 'ios' ? 'iOS' : 'Android'}
                                            </span>
                                        </td>
                                        <td className="runs-cell">{goal.executionCount || 0}</td>
                                        <td className="time-cell">
                                            {goal.lastRunAt ? <TimeAgo date={goal.lastRunAt} /> : 'Never'}
                                        </td>
                                        <td className="actions-cell">
                                            <button
                                                className="icon-btn kebab-btn"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    setActiveActionMenuGoalId(prev => prev === goal.id ? null : goal.id);
                                                }}
                                                title="Actions"
                                            >
                                                <MoreVertical size={16} />
                                            </button>
                                            {activeActionMenuGoalId === goal.id && (
                                                <div className="kebab-dropdown-menu">
                                                    <button
                                                        className="kebab-dropdown-item"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            handleRerun(goal.id, e);
                                                        }}
                                                    >
                                                        <Play size={14} />
                                                        Run
                                                    </button>
                                                    <button
                                                        className="kebab-dropdown-item"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            setSelectedGoalForDuplication(goal);
                                                            setActiveActionMenuGoalId(null);
                                                        }}
                                                    >
                                                        <Copy size={14} />
                                                        Duplicate
                                                    </button>
                                                    <button
                                                        className="kebab-dropdown-item danger"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            handleDelete(goal.id, e);
                                                        }}
                                                    >
                                                        <Trash2 size={14} />
                                                        Delete
                                                    </button>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <Pagination
                        page={pagination.page}
                        totalPages={pagination.totalPages}
                        limit={pagination.limit}
                        onPageChange={handlePageChange}
                        onLimitChange={handleLimitChange}
                        storageKey="rova_limit_goals"
                    />
                </>
            )}

            <Dialog
                isOpen={dialogState.isOpen}
                onClose={closeDialog}
                title={dialogState.title}
                message={dialogState.message}
                type={dialogState.type}
                variant={dialogState.variant}
                confirmText={dialogState.confirmText}
                cancelText={dialogState.cancelText}
                placeholder={dialogState.placeholder}
                defaultValue={dialogState.defaultValue}
                onConfirm={dialogState.onConfirm}
                onCancel={dialogState.onCancel}
            />

            {showAddToPlanModal && (
                <div className="dialog-overlay" onClick={() => setShowAddToPlanModal(false)}>
                    <div className="dialog-container dialog-default plan-modal-width" onClick={(e) => e.stopPropagation()}>
                        <div className="dialog-header">
                            <h3 className="dialog-title">Add Selected Goals to Plan</h3>
                            <button className="dialog-close" onClick={() => setShowAddToPlanModal(false)}>
                                <X size={20} />
                            </button>
                        </div>
                        <div className="dialog-body">
                            <input
                                type="text"
                                className="dialog-input"
                                placeholder="Search existing execution plans..."
                                value={planSearchQuery}
                                onChange={(e) => setPlanSearchQuery(e.target.value)}
                                style={{ marginBottom: '16px' }}
                            />
                            {isSubmittingPlanAction ? (
                                <div className="plans-modal-loading">Adding goals to execution plan...</div>
                            ) : filteredPlans.length === 0 ? (
                                <div className="plans-modal-empty">No matching execution plans found.</div>
                            ) : (
                                <div className="plans-modal-list">
                                    {filteredPlans.map(plan => (
                                        <div
                                            key={plan.id}
                                            className="plans-modal-item"
                                            onClick={() => handleAddToPlan(plan.id)}
                                        >
                                            <div className="plans-modal-item-name">{plan.name}</div>
                                            {plan.description && <div className="plans-modal-item-desc">{plan.description}</div>}
                                            <div className="plans-modal-item-meta">
                                                <span className={`platform-badge platform-${plan.platform}`}>
                                                    {plan.platform === 'ios' ? 'iOS' : 'Android'}
                                                </span>
                                                {plan.app && <span className="plans-modal-item-app">App: {plan.app.name}</span>}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="dialog-footer">
                            <button className="btn btn-ghost btn-sm" onClick={() => setShowAddToPlanModal(false)}>
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showCreatePlanModal && (
                <div className="dialog-overlay" onClick={() => setShowCreatePlanModal(false)}>
                    <div className="dialog-container dialog-default" onClick={(e) => e.stopPropagation()}>
                        <form onSubmit={handleCreateNewPlan}>
                            <div className="dialog-header">
                                <h3 className="dialog-title">Create Execution Plan</h3>
                                <button type="button" className="dialog-close" onClick={() => setShowCreatePlanModal(false)}>
                                    <X size={20} />
                                </button>
                            </div>
                            <div className="dialog-body" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                    <label style={{ fontSize: '13px', fontWeight: '500', color: 'var(--text-muted)' }}>Plan Name</label>
                                    <input
                                        type="text"
                                        className="dialog-input"
                                        required
                                        placeholder="e.g. Daily Android Regression Plan"
                                        value={newPlanName}
                                        onChange={(e) => setNewPlanName(e.target.value)}
                                    />
                                </div>
                                <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                    <label style={{ fontSize: '13px', fontWeight: '500', color: 'var(--text-muted)' }}>Description (Optional)</label>
                                    <textarea
                                        className="dialog-input"
                                        rows={3}
                                        placeholder="Briefly describe the purpose of this plan..."
                                        value={newPlanDescription}
                                        onChange={(e) => setNewPlanDescription(e.target.value)}
                                        style={{ resize: 'vertical' }}
                                    />
                                </div>
                                <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                    <label style={{ fontSize: '13px', fontWeight: '500', color: 'var(--text-muted)' }}>Select App</label>
                                    <select
                                        className="dialog-input"
                                        value={newPlanAppId}
                                        onChange={(e) => setNewPlanAppId(e.target.value)}
                                        required
                                    >
                                        <option value="" disabled>-- Select an App --</option>
                                        {apps
                                            .filter(app => {
                                                const selectedGoals = goals.filter(g => selectedGoalIds.includes(g.id));
                                                const targetPlatform = selectedGoals[0]?.platform;
                                                return !targetPlatform || app.platform === targetPlatform;
                                            })
                                            .map(app => (
                                                <option key={app.id} value={app.id}>
                                                    {app.name} ({app.platform === 'ios' ? 'iOS' : 'Android'})
                                                </option>
                                            ))
                                        }
                                    </select>
                                </div>
                            </div>
                            <div className="dialog-footer">
                                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setShowCreatePlanModal(false)}>
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary btn-sm"
                                    disabled={isSubmittingPlanAction}
                                >
                                    {isSubmittingPlanAction ? 'Creating...' : 'Create Plan'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <DuplicateGoalModal
                isOpen={!!selectedGoalForDuplication}
                onClose={() => setSelectedGoalForDuplication(null)}
                onComplete={() => {
                    alert({
                        title: 'Success',
                        message: 'Test goal duplicated successfully!',
                        variant: 'success'
                    });
                    fetchGoals();
                }}
                goal={selectedGoalForDuplication}
            />

            <BulkCategorizeModal
                isOpen={showBulkCategorizeModal}
                onClose={() => setShowBulkCategorizeModal(false)}
                onComplete={() => {
                    alert({
                        title: 'Success',
                        message: 'Successfully categorized selected test goals!',
                        variant: 'success'
                    });
                    setSelectedGoalIds([]);
                    fetchGoals();
                }}
                goalIds={selectedGoalIds}
                workspaceCategories={workspaceCategories}
            />

            {/* Bulk Run Mode Modal */}
            {showBulkRunModal && (
                <div className="dialog-overlay" onClick={() => !isSubmittingBulkRun && setShowBulkRunModal(false)}>
                    <div className="dialog-container dialog-default plan-modal-width" onClick={(e) => e.stopPropagation()}>
                        <div className="dialog-header">
                            <h3 className="dialog-title">Run Selected Goals ({selectedGoalIds.length})</h3>
                            <button className="dialog-close" onClick={() => setShowBulkRunModal(false)} disabled={isSubmittingBulkRun}>
                                <X size={20} />
                            </button>
                        </div>
                        <div className="dialog-body">
                            <p style={{ marginBottom: '16px', fontSize: '14px', color: 'var(--text-secondary)' }}>
                                Choose how you want to execute the selected test goals:
                            </p>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                <label
                                    onClick={() => setBulkRunMode('individual')}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: '12px',
                                        padding: '14px',
                                        borderRadius: '8px',
                                        border: bulkRunMode === 'individual' ? '2px solid var(--primary, #6366f1)' : '1px solid var(--border)',
                                        background: bulkRunMode === 'individual' ? 'rgba(99, 102, 241, 0.08)' : 'var(--bg-input)',
                                        cursor: 'pointer'
                                    }}
                                >
                                    <input
                                        type="radio"
                                        name="bulkRunMode"
                                        value="individual"
                                        checked={bulkRunMode === 'individual'}
                                        onChange={() => setBulkRunMode('individual')}
                                        style={{ marginTop: '2px' }}
                                    />
                                    <div>
                                        <div style={{ fontWeight: 600, fontSize: '14px', color: 'var(--text-primary)' }}>⚡ Individual Executions</div>
                                        <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '2px' }}>
                                            Run each test goal independently in parallel on separate task queues.
                                        </div>
                                    </div>
                                </label>

                                <label
                                    onClick={() => setBulkRunMode('execution_plan')}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: '12px',
                                        padding: '14px',
                                        borderRadius: '8px',
                                        border: bulkRunMode === 'execution_plan' ? '2px solid var(--primary, #6366f1)' : '1px solid var(--border)',
                                        background: bulkRunMode === 'execution_plan' ? 'rgba(99, 102, 241, 0.08)' : 'var(--bg-input)',
                                        cursor: 'pointer'
                                    }}
                                >
                                    <input
                                        type="radio"
                                        name="bulkRunMode"
                                        value="execution_plan"
                                        checked={bulkRunMode === 'execution_plan'}
                                        onChange={() => setBulkRunMode('execution_plan')}
                                        style={{ marginTop: '2px' }}
                                    />
                                    <div>
                                        <div style={{ fontWeight: 600, fontSize: '14px', color: 'var(--text-primary)' }}>📋 Execution Plan Run (Sequential Session Reuse)</div>
                                        <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '2px' }}>
                                            Bundle goals into an Execution Plan. Executes goals sequentially in a single unified device session with shared context.
                                        </div>
                                    </div>
                                </label>
                            </div>

                            {bulkRunMode === 'execution_plan' && (
                                <div style={{ marginTop: '16px' }}>
                                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '6px', color: 'var(--text-secondary)' }}>
                                        Execution Plan Name (Optional):
                                    </label>
                                    <input
                                        type="text"
                                        placeholder={`Bulk Execution (${selectedGoalIds.length} Goals)`}
                                        value={bulkPlanName}
                                        onChange={(e) => setBulkPlanName(e.target.value)}
                                        style={{
                                            width: '100%',
                                            padding: '10px 12px',
                                            borderRadius: '6px',
                                            border: '1px solid var(--border)',
                                            background: 'var(--bg-input)',
                                            color: 'var(--text-primary)',
                                            fontSize: '13px'
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                        <div className="dialog-footer">
                            <button
                                type="button"
                                className="btn btn-ghost btn-sm"
                                onClick={() => setShowBulkRunModal(false)}
                                disabled={isSubmittingBulkRun}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary btn-sm"
                                onClick={executeBulkRun}
                                disabled={isSubmittingBulkRun}
                            >
                                {isSubmittingBulkRun ? 'Queuing...' : `Run ${selectedGoalIds.length} Goal${selectedGoalIds.length !== 1 ? 's' : ''}`}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default GoalsList;
